import { useLayoutEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CoreArchiveDialogProps {
  entity: "contact" | "account";
  record?: { id: string; archivedAt?: Date | string | null } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onPendingChange?: (pending: boolean) => void;
}

/** Shared recovery behavior for the existing core-record confirmation dialogs. */
export function CoreArchiveDialog({ entity, record, open, onOpenChange, onSuccess, onPendingChange }: CoreArchiveDialogProps) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const inFlight = useRef(false);
  const initiator = useRef<HTMLElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => { if (pending) content.current?.focus(); }, [pending]);
  const action = record?.archivedAt ? "Restore" : "Archive";
  const label = entity === "contact" ? "Contact" : "Account";

  useLayoutEffect(() => {
    if (open) {
      initiator.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setError(undefined);
    }
  }, [open]);

  const submit = async () => {
    if (!record || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    onPendingChange?.(true);
    setError(undefined);
    try {
      await apiRequest("POST", `/api/crm/${entity}s/${record.id}/${action.toLowerCase()}`, {});
      await queryClient.invalidateQueries({ predicate: query =>
        typeof query.queryKey[0] === "string" &&
        ["contacts", "accounts", "client-groups", "deals", "facilities"].some(kind =>
          (query.queryKey[0] as string).startsWith(`/api/crm/${kind}`)) });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      setError(`Could not ${action.toLowerCase()} the ${entity}. Try again.`);
    } finally {
      inFlight.current = false;
      setPending(false);
      onPendingChange?.(false);
    }
  };

  return <AlertDialog open={open} onOpenChange={next => { if (!inFlight.current) onOpenChange(next); }}>
    <AlertDialogContent ref={content} tabIndex={-1} onEscapeKeyDown={event => { if (inFlight.current) event.preventDefault(); }}
      onCloseAutoFocus={event => {
        event.preventDefault();
        requestAnimationFrame(() => {
          const target = initiator.current?.isConnected ? initiator.current :
            document.querySelector<HTMLElement>(`[data-testid="button-add-${entity}"]`);
          target?.focus();
        });
      }}>
      <AlertDialogHeader>
        <AlertDialogTitle>{action} {label}</AlertDialogTitle>
        <AlertDialogDescription>
          {action === "Archive"
            ? "Remove this record from active lists. Its linked records and history stay available. You can restore it later."
            : "Return this record to active lists. Its linked records and history stay available."}
        </AlertDialogDescription>
      </AlertDialogHeader>
      {error && <p role="alert">{error}</p>}
      <AlertDialogFooter>
        <Button type="button" variant="outline" disabled={pending} onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button type="button" disabled={pending} onClick={submit}>{pending ? "Saving…" : `${action} ${label}`}</Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
}
